import {
  Alert,
  ButtonGroup,
  Card,
  copyToClipboard,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Textarea,
} from 'platform/components';
import {Box, Grid, Show, Space, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetWarehousesQuery, usePostArticleCreationStressTestMutation} from '@dms/api/warehouse';
import {
  getOptionsFromCustomersAsSupplier,
  handleApiError,
  useGetSupplierCustomersQuery,
} from '@dms/shared';

import {useBoolean} from 'shared/web';

import {getFixedTranslation} from '../utils/getFixedTranslation';

const formTypeSchema = z.object({
  warehouseId: z.string(),
  supplierId: z.string(),
  volume: z.number().int(),
  oemPattern: z.string().nullish(),
  oemName: z.string().nullish(),
  isTestOnly: z.boolean(),
});

type FormType = z.infer<typeof formTypeSchema>;

export function WarehouseArticleStressTest() {
  const [isOn, , , toggle] = useBoolean();
  const [lastResult, setLastResult] = useState<unknown>(null);

  const {isLoadingWarehouseOptions, warehouseOptions} = useGetWarehousesQuery(undefined, {
    selectFromResult: (result) => ({
      isLoadingWarehouseOptions: result.isLoading,
      warehouseOptions:
        result.data?.map((warehouse) => ({
          label: warehouse.name,
          value: warehouse.id,
        })) || [],
    }),
  });

  const {data: suppliers, isLoading: isLoadingSupplierOptions} = useGetSupplierCustomersQuery();
  const supplierOptions = getOptionsFromCustomersAsSupplier(suppliers);

  const [mutation, mutationStatus] = usePostArticleCreationStressTestMutation();

  const defaultValues: Partial<FormType> = {
    isTestOnly: true,
  };

  const handleSubmit: FormSubmitHandler<FormType> = async (formValues) => {
    await mutation({
      warehouseId: formValues.warehouseId,
      supplierId: formValues.supplierId,
      volume: formValues.volume,
      namePattern: formValues.oemName,
      oemPattern: formValues.oemPattern,
      testOnly: formValues.isTestOnly,
    })
      .unwrap()
      .then((result) => {
        setLastResult(result);
      })
      .catch(handleApiError);
  };

  const formattedResult = isNotNil(lastResult) ? JSON.stringify(lastResult, null, 2) : '';

  const handleCopyResult = () => {
    copyToClipboard(formattedResult);
  };

  return (
    <Card
      control={{
        type: 'switch',
        value: isOn,
        onChange: toggle,
      }}
      title={getFixedTranslation('page.settings.labels.warehouseArticleStressTest')}
      isExpanded={isOn}
      variant="inlineGrey"
      flags={[
        isOn
          ? {
              label: getFixedTranslation('general.labels.on'),
              colorScheme: 'orange',
              isSubtle: true,
            }
          : {
              label: getFixedTranslation('general.labels.off'),
              colorScheme: 'neutral',
              isSubtle: true,
            },
      ]}
    >
      <Form<FormType>
        experimentalZodSchema={formTypeSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Alert
              type="inline"
              variant="warning"
              title={getFixedTranslation(
                'entity.warehouse.labels.warehouseArticleStressTestWarning'
              )}
            />
            <Grid spacing={2} horizontalSpacing={4} columns={2}>
              <FormField
                isRequired
                isLoading={isLoadingWarehouseOptions}
                control={control}
                name="warehouseId"
                type="choice"
                label={getFixedTranslation('entity.warehouse.labels.warehouse')}
                options={warehouseOptions}
              />
              <FormField
                isRequired
                isLoading={isLoadingSupplierOptions}
                control={control}
                name="supplierId"
                type="choice"
                label={getFixedTranslation('entity.warehouse.labels.supplier')}
                options={supplierOptions}
              />
              <FormField
                isRequired
                isStepperVisible
                control={control}
                name="volume"
                type="integer"
                label={getFixedTranslation('entity.warehouse.labels.volume')}
              />
              <FormField
                control={control}
                name="oemPattern"
                type="text"
                label={getFixedTranslation('entity.warehouse.labels.oemPattern')}
              />
              <FormField
                control={control}
                name="oemName"
                type="text"
                label={getFixedTranslation('entity.warehouse.labels.oemName')}
              />
              <Box>
                <Space vertical={6} />
                <FormField
                  control={control}
                  name="isTestOnly"
                  type="checkbox"
                  label={getFixedTranslation('entity.warehouse.labels.isTestOnly')}
                />
              </Box>
            </Grid>

            <ButtonGroup>
              <FormButton
                isLoading={mutationStatus.isLoading}
                control={control}
                type="submit"
                title={getFixedTranslation('entity.warehouse.actions.submit')}
              />
            </ButtonGroup>

            <Show when={isNotNil(lastResult)}>
              <Textarea
                label={getFixedTranslation('entity.warehouse.labels.result')}
                value={formattedResult}
                isDisabled
                isScrollable
                minRows={6}
                maxRows={20}
                labelActions={[
                  {
                    title: getFixedTranslation('general.actions.copyToClipboard'),
                    leftIcon: 'content/copy',
                    onClick: handleCopyResult,
                  },
                ]}
              />
            </Show>
          </VStack>
        )}
      </Form>
    </Card>
  );
}
